// Fast modPow shim for amazon-cognito-identity-js on Hermes (Expo SDK 50+).
//
// The SDK's enhance-rn.js entry point patches BigInteger.prototype.modPow to
// delegate to the native RNAWSCognito module. When that module is not linked
// (Expo New Architecture), it falls back to jsbn's pure-JS implementation
// which takes 7-10 seconds on Hermes for a 3072-bit modPow.
//
// Strategy:
//   1. require() src/BigInteger before enhance-rn.js loads — Metro caches
//      modules, so both refer to the same prototype object.
//   2. Replace modPow with a Hermes-native BigInt implementation.
//   3. When enhance-rn.js evaluates it does:
//        const originalModPow = BigInteger.prototype.modPow   // our fast ver
//        BigInteger.prototype.modPow = function nativeModPow(...) {
//          if (RNAWSCognito) { /* native */ }
//          else { return originalModPow.call(...) }  // calls our fast ver ✓
//        }
//
// This file MUST be the first import in app/_layout.tsx so it runs before
// amazon-cognito-identity-js (which loads via AuthContext → cognito.ts).

import { Platform } from 'react-native'

if (Platform.OS !== 'web' && typeof BigInt !== 'undefined') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const mod = require('amazon-cognito-identity-js/src/BigInteger')
    const BigIntegerCtor = mod?.default ?? mod
    if (typeof BigIntegerCtor?.prototype?.modPow !== 'function') {
      throw new Error('modPow not found')
    }

    const originalModPow: Function = BigIntegerCtor.prototype.modPow

    function modPowBigInt(base: bigint, exp: bigint, m: bigint): bigint {
      let result = 1n
      let b = base % m
      while (exp > 0n) {
        if (exp & 1n) result = (result * b) % m
        exp >>= 1n
        b = (b * b) % m
      }
      return result
    }

    // eslint-disable-next-line max-params
    BigIntegerCtor.prototype.modPow = function fastModPow(
      this: { toString(r: number): string },
      e: { toString(r: number): string },
      m: { toString(r: number): string },
      cb: (err: null, result: unknown) => void
    ) {
      try {
        const baseH = this.toString(16)
        const expH = e.toString(16)
        const modH = m.toString(16)
        if (!baseH || !expH || !modH) throw new Error('empty operand')
        const result = modPowBigInt(BigInt('0x' + baseH), BigInt('0x' + expH), BigInt('0x' + modH))
        cb(null, new BigIntegerCtor(result.toString(16), 16))
      } catch {
        return originalModPow.call(this, e, m, cb)
      }
    }
  } catch {
    // Patching failed — original slow jsbn path will be used.
  }
}
